import { useEffect, useMemo, useState } from "react";
import clsx from "clsx";
import { Button } from "@/components/ui/Button";
import type { Order } from "@/lib/projects";
import type { ProjectSchadensmeldung, ProjectTimesheet } from "@/lib/projectOverview";
import {
  fetchAuftragLieferscheine,
  type AuftragLieferscheinSummary,
} from "@/features/timesheetFlow/timesheetFlowApi";
import { useTimesheetFlow } from "@/features/timesheetFlow/useTimesheetFlow";
import { annotateTimesheetRevisions, type TimesheetSummary } from "@/lib/timesheets";
import { OverviewCard } from "@/projectOverview/OverviewCard";
import {
  buildOverviewEvidenceList,
  formatTimesheetPeriod,
  getCompletionPdfUrl,
  getMuendlichBeauftragtPdfUrl,
  getTimesheetRowTitle,
  isSupersededTimesheet,
  translateTimesheetStatus,
} from "@/projectOverview/timesheetDisplay";
import { StoredMailBeauftragungen } from "@/projectOverview/StoredMailBeauftragungen";
import { useFestpreisProject } from "@/projectOverview/useFestpreisProject";

function canCreateMuendlichBeauftragt(project: Order): boolean {
  if (project.muendlich_beauftragt_at) return false;
  if (project.angebot_commission_status === "beauftragt") return false;
  if (project.angebot_commission_status === "abgelehnt") return false;
  if (project.commissioned_gd_document_id) return false;
  return true;
}

function fmtDateTime(value: string | null | undefined): string {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function translateLieferscheinStatus(status: string | null | undefined): string {
  const n = String(status || "")
    .trim()
    .toLowerCase();
  if (n === "erstellt") return "Erstellt";
  if (n === "versendet") return "Versendet";
  if (n === "geliefert") return "Geliefert";
  if (n === "entwurf") return "Entwurf";
  return status ? String(status) : "";
}

export type TimesheetsSectionVariant = "default" | "timesheets" | "completions";

export function TimesheetsSection({
  project,
  timesheets,
  schadensmeldungen = [],
  canCreate,
  variant = "default",
}: {
  project: Order;
  timesheets: ProjectTimesheet[];
  schadensmeldungen?: ProjectSchadensmeldung[];
  canCreate: boolean;
  /** default = Modal; timesheets/completions = Projektseite */
  variant?: TimesheetsSectionVariant;
}) {
  const {
    openCreateForProject,
    openMuendlichBeauftragt,
    openEdit,
    openDetail,
    openLieferscheinDetail,
    refreshKey,
  } = useTimesheetFlow();
  const festpreisMode = useFestpreisProject(project);
  const [lieferscheine, setLieferscheine] = useState<AuftragLieferscheinSummary[]>([]);

  useEffect(() => {
    if (!festpreisMode || !project.id) {
      setLieferscheine([]);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const rows = await fetchAuftragLieferscheine(project.id);
        if (!cancelled) setLieferscheine(rows);
      } catch {
        if (!cancelled) setLieferscheine([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [festpreisMode, project.id, refreshKey]);

  const heading =
    variant === "completions" || festpreisMode ? "Fertigstellungsmeldungen" : "Stundennachweise";
  const completed = !!project.project_completed;
  const showMuendlich =
    canCreate &&
    variant !== "completions" &&
    canCreateMuendlichBeauftragt(project);

  const annotatedTimesheets = useMemo(
    () =>
      annotateTimesheetRevisions(timesheets as TimesheetSummary[]) as ProjectTimesheet[],
    [timesheets],
  );

  const evidence = useMemo(() => {
    const all = buildOverviewEvidenceList(project, annotatedTimesheets, schadensmeldungen);
    if (variant === "timesheets") {
      return all.filter(
        (item) =>
          item.kind === "timesheet" ||
          item.kind === "schadensmeldung" ||
          item.kind === "muendlichBeauftragt",
      );
    }
    if (variant === "completions") {
      if (festpreisMode) {
        return all.filter(
          (item) => item.kind !== "schadensmeldung" && item.kind !== "muendlichBeauftragt",
        );
      }
      return all.filter((item) => item.kind === "completion");
    }
    return all;
  }, [project, annotatedTimesheets, schadensmeldungen, variant, festpreisMode]);

  if (variant === "timesheets" && festpreisMode) {
    return null;
  }

  const showCreate =
    canCreate &&
    (festpreisMode
      ? !completed
      : !completed || project.late_hour_permission === true);
  const showLieferscheine = festpreisMode && (variant === "default" || variant === "completions");
  const hasAnyList = evidence.length > 0 || (showLieferscheine && lieferscheine.length > 0);

  const emptyLabel =
    festpreisMode || variant === "completions"
      ? "Keine Fertigstellungsmeldungen vorhanden."
      : "Keine Stundennachweise vorhanden.";

  return (
    <OverviewCard title={heading}>
      {showCreate || showMuendlich ? (
        <div className="mb-3 flex flex-wrap gap-2">
          {showCreate ? (
            <Button type="button" size="sm" onClick={() => openCreateForProject(project)}>
              Neu
            </Button>
          ) : null}
          {showMuendlich ? (
            <Button
              type="button"
              size="sm"
              variant="secondary"
              onClick={() => openMuendlichBeauftragt(project)}
            >
              Mündlich beauftragt
            </Button>
          ) : null}
        </div>
      ) : null}

      {variant !== "completions" ? <StoredMailBeauftragungen orderId={project.id} /> : null}

      {!hasAnyList ? (
        <p className="text-sm text-label-tertiary">{emptyLabel}</p>
      ) : (
        <ul className="eh-po-timesheet-list">
          {showLieferscheine
            ? lieferscheine.map((ls) => {
                const when = fmtDateTime(ls.created_at);
                const statusLabel = translateLieferscheinStatus(ls.status);
                const parts = [
                  when || null,
                  statusLabel || null,
                  ls.has_open_rueckstand ? "offener Rückstand" : null,
                ].filter(Boolean);
                const title = ls.dokument_nummer
                  ? `Lieferschein ${ls.dokument_nummer}`
                  : `Lieferschein #${ls.id}`;
                return (
                  <li key={`lieferschein-${ls.id}`} className="eh-po-timesheet-row">
                    <div className="eh-po-timesheet-meta">
                      <div className="eh-po-timesheet-title">{title}</div>
                      <div className="eh-po-timesheet-subtitle">
                        {parts.length ? parts.join(" · ") : "—"}
                      </div>
                    </div>
                    <div className="eh-po-timesheet-actions">
                      <button
                        type="button"
                        className="eh-po-timesheet-action eh-po-timesheet-action--primary"
                        onClick={() => openLieferscheinDetail(ls.id, project.id)}
                      >
                        Lieferschein anzeigen
                      </button>
                    </div>
                  </li>
                );
              })
            : null}

          {evidence.map((item) => {
            if (item.kind === "completion") {
              const p = item.project;
              const parts: string[] = [];
              const when = fmtDateTime(p.project_completion_created_at);
              if (when) parts.push(when);
              const by = p.project_completion_created_by;
              if (by) parts.push(`von ${by}`);
              const pdfUrl = getCompletionPdfUrl(p);
              return (
                <li key="completion" className="eh-po-timesheet-row">
                  <div className="eh-po-timesheet-meta">
                    <div className="eh-po-timesheet-title">Fertigstellungsmeldung</div>
                    <div className="eh-po-timesheet-subtitle">
                      {parts.length ? parts.join(" · ") : "—"}
                    </div>
                  </div>
                  <div className="eh-po-timesheet-actions">
                    {pdfUrl ? (
                      <a
                        href={pdfUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="eh-po-timesheet-action"
                      >
                        PDF anzeigen
                      </a>
                    ) : (
                      <span className="text-xs text-label-tertiary">
                        Kein öffentlicher Link verfügbar.
                      </span>
                    )}
                  </div>
                </li>
              );
            }

            if (item.kind === "muendlichBeauftragt") {
              const p = item.project;
              const parts: string[] = [];
              const when = fmtDateTime(p.muendlich_beauftragt_at);
              if (when) parts.push(when);
              if (p.muendlich_beauftragt_von) parts.push(`von ${p.muendlich_beauftragt_von}`);
              const pdfUrl = getMuendlichBeauftragtPdfUrl(p);
              return (
                <li key="muendlich-beauftragt" className="eh-po-timesheet-row">
                  <div className="eh-po-timesheet-meta">
                    <div className="eh-po-timesheet-title">Mündliche Beauftragung</div>
                    <div className="eh-po-timesheet-subtitle">
                      {parts.length ? parts.join(" · ") : "—"}
                      {p.muendlich_beauftragt_notiz ? (
                        <span className="mt-0.5 block text-label-tertiary">
                          Notiz: {p.muendlich_beauftragt_notiz}
                        </span>
                      ) : null}
                    </div>
                  </div>
                  <div className="eh-po-timesheet-actions">
                    {pdfUrl ? (
                      <a
                        href={pdfUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="eh-po-timesheet-action"
                      >
                        PDF anzeigen
                      </a>
                    ) : (
                      <span className="text-xs text-label-tertiary">
                        Kein öffentlicher Link verfügbar.
                      </span>
                    )}
                  </div>
                </li>
              );
            }

            if (item.kind === "schadensmeldung") {
              const sm = item.item;
              const parts: string[] = [];
              const when = fmtDateTime(sm.created_at);
              if (when) parts.push(when);
              if (sm.created_by_display) parts.push(`von ${sm.created_by_display}`);
              const viewUrl = sm.file_url || sm.sharepoint_web_url || null;
              return (
                <li key={`schadensmeldung-${sm.id}`} className="eh-po-timesheet-row">
                  <div className="eh-po-timesheet-meta">
                    <div className="eh-po-timesheet-title">Schadensmeldung / FM-Auftrag</div>
                    <div className="eh-po-timesheet-subtitle">
                      {[sm.filename, parts.length ? parts.join(" · ") : null]
                        .filter(Boolean)
                        .join(" · ") || "—"}
                    </div>
                  </div>
                  <div className="eh-po-timesheet-actions">
                    {viewUrl ? (
                      <a
                        href={viewUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="eh-po-timesheet-action"
                      >
                        Datei anzeigen
                      </a>
                    ) : (
                      <span className="text-xs text-label-tertiary">
                        Kein Link verfügbar.
                      </span>
                    )}
                  </div>
                </li>
              );
            }

            const ts = item.ts;
            const title = getTimesheetRowTitle(ts);
            const superseded = isSupersededTimesheet(ts);
            const period = formatTimesheetPeriod(ts);
            const statusLabel = superseded
              ? "Abgelöst · nicht mehr gültig"
              : translateTimesheetStatus(ts.status_display || ts.status);
            const subtitle = [period, statusLabel].filter(Boolean).join(" · ");
            const isDraft =
              !superseded &&
              (String(ts.status || "draft").toLowerCase() === "draft" ||
                !String(ts.status || "").trim());

            return (
              <li
                key={ts.id}
                className={clsx(
                  "eh-po-timesheet-row",
                  superseded && "eh-po-timesheet-row--superseded",
                )}
              >
                <div className="eh-po-timesheet-meta">
                  <div className="eh-po-timesheet-title">
                    <span className={clsx(superseded && "eh-po-timesheet-title-text--superseded")}>
                      {title}
                    </span>
                    {superseded ? (
                      <span className="eh-po-timesheet-badge eh-po-timesheet-badge--superseded">
                        Abgelöst
                      </span>
                    ) : null}
                  </div>
                  <div className="eh-po-timesheet-subtitle">{subtitle || "—"}</div>
                </div>
                <div className="eh-po-timesheet-actions">
                  {isDraft ? (
                    <button
                      type="button"
                      className="eh-po-timesheet-action eh-po-timesheet-action--primary"
                      onClick={() => openEdit(ts.id)}
                    >
                      Entwurf bearbeiten
                    </button>
                  ) : null}
                  {!isDraft ? (
                    <button
                      type="button"
                      className="eh-po-timesheet-action"
                      onClick={() => openDetail(ts.id)}
                    >
                      Nachweis anzeigen
                    </button>
                  ) : null}
                </div>
              </li>
            );
          })}
        </ul>
      )}

    </OverviewCard>
  );
}
